import { Injectable } from '@angular/core';
import { Observable, of, from, forkJoin } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import moment from 'moment';
import { PapiService } from './papi.service';
import { Camera, FlightInfoParams, GeofenceParams, Group, HomeLocationParams, OrgTree, Organization, Perimeter, Sensor, SensorList, TrackInfoParams } from '../models/plugin-config';
import { ApiService } from './api.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { FloorUserResponse, FloorUsersRequest } from '../models/floor-user.model';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private _sensorList: SensorList | null = null;
  private _orgTree: OrgTree | null = null;
  private _organizations: Organization[] | null = null;
  private readonly _paginate = { paginate: true };
  myOrgs: any;
  methods: any;
  sensorList: any;

  constructor(private papi: PapiService, private api: ApiService) { }
  getFlightInfoByFlightNumber(
    organizationId: string,
    airline: string,
    flightNumber: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    const params: FlightInfoParams = {
      organizationId,
      type: 'FLIGHT_NUMBER',
      query: {
        airline,
        flightNumber,
        startDate,
        endDate
      }
    };

    const id = `findFlightawareSearchflight-${Math.random()}`;
    return from(this.papi.request(id, 'db', 'findFlightawareSearchflight', { body: params }));
  }

  getFlightInfoByTailNumber(
    organizationId: string,
    airline: string,
    tailNumber: string,
    startDate: string,
    endDate: string
  ): Observable<any> {
    const params: FlightInfoParams = {
      organizationId,
      type: 'TAIL_NUMBER',
      query: {
        airline,
        tailNumber,
        startDate,
        endDate
      }
    };

    const id = `findFlightawareSearchflight-${Math.random()}`;
    return from(this.papi.request(id, 'db', 'findFlightawareSearchflight', { body: params }));
  }

  getTrackInfo(organizationId: string, faFlightID: string): Observable<any> {
    const params: TrackInfoParams = {
      organizationId,
      flightawareFlightId: faFlightID
    };

    const id = `findFlightawareTrackflight-${Math.random()}`;
    return from(this.papi.request(id, 'db', 'findFlightawareTrackflight', { body: params }));
  }

  getAllHomeLocations(): Observable<any> {
    const params: HomeLocationParams = {
      attributes: ['userId', 'position', 'country', 'region', 'place'],
      limit: 10000
    };

    const id = `findUserAddressFromOrganization-${Math.random()}`;
    return from(this.papi.request(id, 'db', 'findUserAddressFromOrganization', { body: params }, this._paginate));
  }

  getHomeLocationsInGeofence(geo: any): Observable<any> {
    const params: GeofenceParams = {
      geometry: geo,
      useArea: false
    };

    const id = `findUserWithGeofence-${Math.random()}`;
    return from(this.papi.request(id, 'db', 'findUserWithGeofence', { body: params }, this._paginate));
  }


  getUserGeofence(geo: any): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL + 'db/' + ConfigurationManagerService.settings.UserGeofence;

    const params = {
      geometry: geo,
      useArea: false,
      limit: 10000,
    };

    return this.api.post<any>(url, params).pipe(
      catchError((error) => {
        console.error('Error fetching home locations in geofence:', error);
        return of(null); // Return null on error
      })
    );
  }

  getOrgTree() {
    if (this._orgTree) {
      return of(this._orgTree);
    }

    const startTime = Date.now();
    const organizations$ = this.getOrganizations();
    const groups$ = from(this.papi.request<Group>(`getOrgTree-findGroup${Math.random()}`, 'db', 'findGroup', null, this._paginate));

    return organizations$.pipe(
      switchMap(organizations => groups$.pipe(
        map(groups => {
          this._orgTree = {
            organizations: [],
            groups: groups.pipe.rows,
            selectedOrgs: organizations!.map(org => org.id)
          };
          return this._orgTree;
        })
      )),
      catchError(error => {
        console.error('Error getting Org Tree', error);
        throw error;
      })
    );
  }

  getOrganizations() {
    if (this._organizations) {
      return of(this._organizations);
    }

    const params = {
      body: {
        where: {
          id: { $notIn: ['1', '2', '7'] }
        },
        order: [['id', 'ASC']]
      }
    };

    return from(this.papi.request<Organization>(`getOrgTree-findOrganization${Math.random()}`, 'db', 'findOrganization', params, this._paginate)).pipe(
      map(response => {
        this._organizations = response.pipe.rows;
        return this._organizations;
      })
    );
  }



  getSensorList(): Observable<any> {
    if (this._sensorList) {
      return of(this._sensorList);
    }

    // Replace with your actual API base URL
    const baseUrl = ConfigurationManagerService.settings.BaseURL;

    // 1. Get sensors
    return this.api.post<any>(`${baseUrl}db/Sensor/findAndCountAll`, {}).pipe(
      switchMap(results => {
        const sensorInfo = results.rows;
        const perimeterIds = [...new Set(sensorInfo.map((s: { perimeterId: any; }) => s.perimeterId))];
        const sensorIds = [...new Set(sensorInfo.filter((s: { type: string; }) => s.type === 'CAMERA').map((s: { id: any; }) => s.id))];

        const perimeterParams = {
          attributes: ['id', 'parentId', 'ancestors', 'descendants', 'type', 'label'],
          where: {
            $or: [
              { descendants: { $overlap: perimeterIds } },
              { id: perimeterIds }
            ]
          }
        };

        const cameraParams = {
          where: {
            sensorId: sensorIds
          }
        };

        // 2. Get perimeters and cameras in parallel, and pass sensorInfo along
        return forkJoin([
          this.api.post<any>(`${baseUrl}db/Perimeter/findAndCountAll`, perimeterParams),
          this.api.post<any>(`${baseUrl}db/Camera/findAndCountAll`, cameraParams),
          of(sensorInfo)
        ]);
      }),
      map(([perimeters, cameras, sensorInfo]) => {
        // You may need to adjust the path to rows depending on your API response
        const perimeterInfo: Perimeter[] = perimeters?.rows || [];
        const cameraInfo: Camera[] = cameras?.rows || [];

        perimeterInfo.forEach((p) => {
          if (p.type === 'BUILDING') {
            p.labelShort = p.label.slice(0, 18);
          } else if (p.type === 'FLOOR') {
            p.labelShort = p.label.slice(0, 10);
          } else if (p.type === 'ROOM') {
            p.labelShort = p.label.slice(0, 10);
          }
        });
        // Parse camera info (implement this function as needed)
        const cameraList1 = this.parseCameraInfo(cameraInfo, sensorInfo, perimeterInfo);

        // 3. Get organizations by building perimeter IDs
        const buildingPerimeterIds = _.uniq(cameraList1.buildings.map((b: any) => b.id));
        return { cameraList1, buildingPerimeterIds };
      }),
      switchMap(({ cameraList1, buildingPerimeterIds }) => {
        // Get organizations by perimeter IDs
        const orgParam = {
          body: {
            attributes: ['perimeterId', 'organizationId'],
            where: { perimeterId: buildingPerimeterIds },
            limit: 10000
          }
        };
        return this.api.post<any>(
          `${ConfigurationManagerService.settings.BaseURL}db/OrganizationPerimeter/findAndCountAll`,
          orgParam
        ).pipe(
          map(orgResults => ({ cameraList1, orgResults }))
        );
      }),
      map(({ cameraList1, orgResults }) => {
        // Group orgs by perimeterId
        const orgsForPerimeter = _.groupBy(orgResults.rows, 'perimeterId');
        Object.keys(orgsForPerimeter).forEach(key => {
          orgsForPerimeter[key] = orgsForPerimeter[key].map((o: any) => o.organizationId);
        });

        // Attach organizationIds to cameras and buildings
        cameraList1.cameraList.forEach((c: any) => {
          c.building.organizationIds = orgsForPerimeter[c.building.id];
        });
        cameraList1.buildings.forEach((b: any) => {
          b.organizationIds = orgsForPerimeter[b.id];
        });

        // Get all floors for all buildings
        let floorPerimeters: any[] = [];
        cameraList1.buildings.forEach((building: any) => {
          floorPerimeters = floorPerimeters.concat(building.floors);
        });
        const floorPerimeterIds = floorPerimeters.map((f: any) => f.id);

        return { cameraList1, floorPerimeterIds };
      }),
      switchMap(({ cameraList1, floorPerimeterIds }) => {
        // Get floor info
        const floorParam = {
          body: {
            where: { perimeterId: floorPerimeterIds }
          }
        };
        return this.api.post<any>(
          `${ConfigurationManagerService.settings.BaseURL}db/Floor/findAndCountAll`,
          floorParam
        ).pipe(
          map(floorResults => ({ cameraList1, floorResults }))
        );
      }),
      map(({ cameraList1, floorResults }) => {
        // Build floor number dictionary
        const floorNumberDictionary: { [key: string]: any } = {};
        floorResults.rows.forEach((floor: any) => {
          floorNumberDictionary[floor.perimeterId] = floor.number;
        });

        // Attach floor numbers to buildings, floors, and cameras
        cameraList1.buildings.forEach((b: any) => {
          b.floors.forEach((floor: any) => {
            floor.number = floorNumberDictionary[floor.id];
          });
        });
        cameraList1.cameraList.forEach((c: any) => {
          if (c.floor) c.floor.number = floorNumberDictionary[c.floor.id];
        });

        this._sensorList = cameraList1;
        return this._sensorList;
      })
    );
  }

  parseCameraInfo(cameraInfo: any, sensorInfo: any, perimeterInfo: any) {
    _.forEach(sensorInfo, (sensor) => {
      sensor.camera = _.filter(cameraInfo, (o) => {
        return o.sensorId === sensor.id;
      });
    });
    // get buildings
    var buildings = _.filter(perimeterInfo, (o) => {
      return o.type === 'BUILDING';
    });
    _.forEach(buildings, (building) => {
      building.numberCamera = 0;
      building.numberAccessControl = 0;
      building.numberRaspberryPi = 0;
      building.attachedCameras = _.filter(sensorInfo, (o) => {
        return o.perimeterId === building.id && o.type === 'CAMERA';
      });
      building.attachedRaspberryPis = _.filter(sensorInfo, (o) => {
        return o.perimeterId === building.id && o.type === 'RASPBERRY_PI';
      });
      // #477 - support generic sensors.  Camera vs Everything else
      building.attachedAccessControls = _.filter(sensorInfo, (o) => {
        return o.perimeterId === building.id && o.type !== 'CAMERA' && o.type !== 'RASPBERRY_PI';
      });
      building.floors = _.filter(perimeterInfo, (o) => {
        return o.type === 'FLOOR' && _.includes(building.descendants, o.id);
      });
      _.forEach(building.floors, (floor) => {
        floor.attachedCameras = _.filter(sensorInfo, (o) => {
          return o.perimeterId === floor.id && o.type === 'CAMERA';
        });
        floor.attachedRaspberryPis = _.filter(sensorInfo, (o) => {
          return o.perimeterId === floor.id && o.type === 'RASPBERRY_PI';
        });
        // #477 - support generic sensors.  Camera vs Everything else
        floor.attachedAccessControls = _.filter(sensorInfo, (o) => {
          return o.perimeterId === floor.id && o.type !== 'CAMERA' && o.type !== 'RASPBERRY_PI';
        });
        floor.numberCamera = floor.attachedCameras.length;
        floor.numberRaspberryPi = floor.attachedRaspberryPis.length;
        floor.numberAccessControl = floor.attachedAccessControls.length;
        floor.numberSccessControl = floor.attachedAccessControls.length;
        floor.rooms = perimeterInfo.filter((o: any) => {
          return o.type === 'ROOM' && _.includes(floor.descendants, o.id);
        });
        _.forEach(floor.rooms, (room) => {
          room.attachedCameras = _.filter(sensorInfo, (o) => {
            return o.perimeterId === room.id && o.type === 'CAMERA';
          });
          room.attachedRaspberryPis = _.filter(sensorInfo, (o) => {
            return o.perimeterId === room.id && o.type === 'RASPBERRY_PI';
          });
          // #477 - support generic sensors.  Camera vs Everything else
          room.attachedAccessControls = _.filter(sensorInfo, (o) => {
            return o.perimeterId === room.id && o.type !== 'CAMERA' && o.type !== 'RASPBERRY_PI';
          });
          room.numberCamera = room.attachedCameras.length;
          room.numberRaspberryPi = room.attachedRaspberryPis.length;
          room.numberAccessControl = room.attachedAccessControls.length;
          floor.numberCamera += room.attachedCameras.length;
          floor.numberRaspberryPi += room.attachedRaspberryPis.length;
          floor.numberAccessControl += room.attachedAccessControls.length;
        });
        building.numberCamera += floor.numberCamera;
        building.numberRaspberryPi += floor.numberRaspberryPi;
        building.numberAccessControl += floor.numberAccessControl;
      });
    });

    // get pure list of cameras etc
    var cameraList: any[] = [];
    var accessControlList: any[] = [];
    var raspberryPiList: any[] = [];
    _.forEach(buildings, (building) => {
      _.forEach(building.attachedCameras, (camera) => {
        camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
        cameraList.push(camera);
      });
      _.forEach(building.attachedRaspberryPis, (rpi) => {
        rpi.building = { id: building.id, label: building.label, labelShort: building.labelShort };
        raspberryPiList.push(rpi);
      });
      _.forEach(building.attachedAccessControls, (camera) => {
        camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
        accessControlList.push(camera);
      });

      _.forEach(building.floors, (floor) => {
        _.forEach(floor.attachedCameras, (camera) => {
          camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
          camera.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
          cameraList.push(camera);
        });
        _.forEach(floor.attachedRaspberryPis, (rpi) => {
          rpi.building = { id: building.id, label: building.label, labelShort: building.labelShort };
          rpi.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
          raspberryPiList.push(rpi);
        });
        _.forEach(floor.attachedAccessControls, (camera) => {
          camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
          camera.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
          accessControlList.push(camera);
        });
        _.forEach(floor.rooms, (room) => {
          _.forEach(room.attachedCameras, (camera) => {
            camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
            camera.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
            camera.room = { id: room.id, label: room.label, labelShort: room.labelShort };
            camera.buildingLabel = building.label;
            camera.floorLabel = floor.label;
            camera.roomLabel = room.label;
            cameraList.push(camera);
          });
          _.forEach(room.attachedRaspberryPis, (rpi) => {
            rpi.building = { id: building.id, label: building.label, labelShort: building.labelShort };
            rpi.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
            rpi.room = { id: room.id, label: room.label, labelShort: room.labelShort };
            rpi.buildingLabel = building.label;
            rpi.floorLabel = floor.label;
            rpi.roomLabel = room.label;
            raspberryPiList.push(rpi);
          });
          _.forEach(room.attachedAccessControls, (camera) => {
            camera.building = { id: building.id, label: building.label, labelShort: building.labelShort };
            camera.floor = { id: floor.id, label: floor.label, labelShort: floor.labelShort };
            camera.room = { id: room.id, label: room.label, labelShort: room.labelShort };
            camera.buildingLabel = building.label;
            camera.floorLabel = floor.label;
            camera.roomLabel = room.label;
            accessControlList.push(camera);
          });
        });
      });
    });

    _.forEach(cameraList, (c) => {
      c.labelShort = c.label.slice(0, 15);
    });
    cameraList = _.sortBy(cameraList, ['buildingLabel', 'floorLabel', 'roomLabel', 'label']);

    _.forEach(raspberryPiList, (c) => {
      c.labelShort = c.label.slice(0, 15);
    });
    raspberryPiList = _.sortBy(raspberryPiList, ['buildingLabel', 'floorLabel', 'roomLabel', 'label']);

    _.forEach(accessControlList, (c) => {
      c.labelShort = c.label.slice(0, 15);
    });
    accessControlList = _.sortBy(accessControlList, ['buildingLabel', 'floorLabel', 'roomLabel', 'label']);

    return { buildings: buildings, cameraList: cameraList, accessControlList: accessControlList, raspberryPiList: raspberryPiList };
  }
  getUserOnFloorOnMonitorMode(floorUsersRequest: FloorUsersRequest): Observable<FloorUserResponse> {
    const url = ConfigurationManagerService.settings.TezAddPapiBaseUrl + `floor/users/`;
    const body = {
      gridSize: floorUsersRequest.gridSize,
      threshold: floorUsersRequest.threshold,
      bbox: floorUsersRequest.bbox,
      floorPerimeterId: floorUsersRequest.floorPerimeterId,
      floorType: floorUsersRequest.floorType,
    };
    return this.api.post(url, body);
  }

  createUpdate(
    eventId: string,
    description: string,
    lat: number,
    lon: number,
    attachmentData?: any,
    perimeterId?: string
  ): Observable<any> {
    const params: any = {
      incidentId: eventId,
      description,
      position: {
        type: 'Point',
        coordinates: [lon, lat]
      }
    };

    if (attachmentData !== undefined) {
      params.file = attachmentData;
    }
    if (perimeterId) {
      params.perimeterId = perimeterId;
    }
    const url = `${ConfigurationManagerService.settings.BaseURL}db/IncidentUpdate/createFromOrganization`;
    return this.api.post<any>(url, params);
  }

  findOrClusterUsersByBbox(floorUsersRequest: FloorUsersRequest): Observable<any> {
    const url = `${ConfigurationManagerService.settings.BaseURL}db/Incident/${floorUsersRequest.incidentId}/findOrClusterUsersByBbox`;
    const body = {
      gridSize: floorUsersRequest.gridSize,
      threshold: floorUsersRequest.threshold,
      bbox: floorUsersRequest.bbox,
      floorType: floorUsersRequest.floorType,
      floorPerimeterId: floorUsersRequest.floorPerimeterId,
      statuses: floorUsersRequest.statuses
    };
    return this.api.post<any>(url, body);
  }

  getActionSessionForCreatePerimeter(): Observable<any> {
    const baseUrl = ConfigurationManagerService.settings.BaseURL;
    const actionUrl = baseUrl + 'db/Action/sessionWithOrganizationIds';

    const paramPublicPerimeter = {
      where: { code: 'papi.db.Incident.createPerimeterPublic' }
    };
    const paramPrivatePerimeter = {
      where: { code: 'papi.db.Incident.createPerimeter' }
    };
    const paramPublicGeofence = {
      where: { code: 'papi.db.Incident.createGeofencePublic' }
    };
    const paramPrivateGeofence = {
      where: { code: 'papi.db.Incident.createGeofence' }
    };

    return forkJoin({
      publicPerimeter: this.api.post(actionUrl, paramPublicPerimeter),
      privatePerimeter: this.api.post(actionUrl, paramPrivatePerimeter),
      publicGeofence: this.api.post(actionUrl, paramPublicGeofence),
      privateGeofence: this.api.post(actionUrl, paramPrivateGeofence)
    });
  }
  getOrganizationIncidentCategoryInfo() {
    const url1 = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.OrganizationIncidentCategory;
    const url2 = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.IncidentCategoryInfo;

    // First, get all organization incident categories
    const body1 = {
      attributes: ['id', 'organizationId', 'incidentCategoryId'],
      where: {},
      limit: 10000
    };

    return this.api.post<any>(url1, body1).pipe(
      switchMap((results1) => {
        const organizationIncidentCategory = results1.rows || results1.data?.rows || [];
        const incidentCategoryIds = organizationIncidentCategory.map((oic: any) => oic.incidentCategoryId);

        if (!incidentCategoryIds.length) {
          return of({});
        }

        // Now get all incident categories by those IDs
        const body2 = {
          where: { id: incidentCategoryIds },
          limit: 10000
        };
        return this.api.post<any>(url2, body2).pipe(
          map((results2) => {
            const incidentCategories = results2.rows || results2.data?.rows || [];
            const organizationIncidentCategoryInfo: Record<string, any> = {};

            organizationIncidentCategory.forEach((oic: any) => {
              organizationIncidentCategoryInfo[oic.id] = { ...oic };
              for (const ic of incidentCategories) {
                if (ic.id === oic.incidentCategoryId) {
                  organizationIncidentCategoryInfo[oic.id].incidentCategory = ic;
                  break;
                }
              }
            });

            return organizationIncidentCategoryInfo;
          })
        );
      })
    );
  }
  findActiveAlert(time?: string): Observable<any[]> {
    let alerts: any[] = [];
    let now = moment().toISOString();

    let body: any;
    if (time) {
      body = {
        where: {
          updatedAt: { $gt: time }
        },
        order: [['createdAt', 'DESC']]
      };
    } else {
      body = {
        where: {
          expiresAt: { $gt: now },
          dismissedAt: { $gt: now }
        },
        order: [['createdAt', 'DESC']]
      };
    }

    // First API call: get alerts
    return this.api.post<any>(
      `${ConfigurationManagerService.settings.BaseURL}db/Alert/findAndCountAllWithAlertFeatures`,
      body
    ).pipe(
      switchMap(results => {
        alerts = results.rows || results.data?.rows || [];
        const allSensorIds = _.filter(_.map(alerts, 'sensorId'), o => !!o);

        if (!allSensorIds.length) {
          // No sensors to look up, return alerts as is
          return of(alerts);
        }

        // Second API call: get sensors by IDs
        const sensorBody = {
          attributes: ['perimeterId'],
          where: { id: allSensorIds },
          limit: 10000
        };
        return this.api.post<any>(
          `${ConfigurationManagerService.settings.BaseURL}db/Sensor/findAndCountAll`,
          sensorBody
        ).pipe(
          map(sensorResults => {
            const sensors = sensorResults.rows || sensorResults.data?.rows || [];
            alerts.forEach(alert => {
              alert.sensorPerimeterIds = [];
              sensors.forEach((sensor: any) => {
                if (alert.sensorId && alert.sensorId === sensor.id) {
                  alert.sensorPerimeterIds.push(sensor.perimeterId);
                }
              });
            });
            return alerts;
          })
        );
      })
    );
  }
  findDismissedAlert(page: number, keyword?: string, oics?: any[]): Observable<{ data: any[]; count: number }> {
    const numPerPage = 25;
    const future = moment().add(100, 'days').toISOString();
    const offset = (page - 1) * numPerPage;

    const where: any = {
      $or: [
        { expiresAt: { $lt: future } },
        { dismissedAt: { $lt: future } }
      ]
    };

    if (keyword) {
      const ilike = '%' + keyword.toLowerCase() + '%';
      where.name = { $iLike: ilike };
    }

    if (oics && oics.length > 0) {
      where.organizationIncidentCategoryId = oics;
    }

    const body = {
      where,
      order: [['createdAt', 'DESC']],
      offset,
      limit: numPerPage
    };

    // First API call: get dismissed alerts
    return this.api.post<any>(
      `${ConfigurationManagerService.settings.BaseURL}db/Alert/findAndCountAllWithAlertFeatures`,
      body
    ).pipe(
      switchMap(results => {
        const alerts: { data: any[]; count: number } = {
          data: results.rows || results.data?.rows || [],
          count: results.count || results.data?.count || 0
        };
        const allSensorIds = _.filter(_.map(alerts.data, 'sensorId'), o => !!o);

        if (!allSensorIds.length) {
          // No sensors to look up, return alerts as is
          return of(alerts);
        }

        // Second API call: get sensors by IDs
        const sensorBody = {
          attributes: ['perimeterId'],
          where: { id: allSensorIds }
        };
        return this.api.post<any>(
          `${ConfigurationManagerService.settings.BaseURL}db/Sensor/findAndCountAll`,
          sensorBody
        ).pipe(
          map(sensorResults => {
            const sensors = sensorResults.rows || sensorResults.data?.rows || [];
            alerts.data.forEach(alert => {
              alert.sensorPerimeterIds = [];
              sensors.forEach((sensor: any) => {
                if (alert.sensorId && alert.sensorId === sensor.id) {
                  alert.sensorPerimeterIds.push(sensor.perimeterId);
                }
              });
            });
            return alerts;
          })
        );
      })
    );
  }

  getCameraSessionsForIncidentPlayback(cameraIds: string[], startTime: string, endTime: string): Observable<any> {
    const param = {
      where: {
        cameraId: cameraIds,
        endTime: { $ne: null },
        mode: 'LIVE',
        $not: {
          $or: [
            {
              startTime: { $lt: startTime },
              endTime: { $lt: startTime }
            },
            {
              startTime: { $gt: endTime },
              endTime: { $gt: endTime }
            }
          ]
        }
      },
      order: [['id', 'DESC']]
    };
    const url = `${ConfigurationManagerService.settings.BaseURL}db/CameraSession/findAndCountAll`;
    return this.api.post<any>(url, param);
  }

  getFloorUserInBBoxPlayback(
    incidentId: string,
    timestamp: number,
    gridSize: number,
    threshold: number,
    bbox: number[],
    floorPerimeterId: string,
    floorType: string,
    statuses: string[]
  ): Observable<any> {
    const param = {
      timestamp: timestamp,
      gridSize: gridSize,
      threshold: threshold,
      bbox: bbox,
      floorPerimeterId: floorPerimeterId,
      floorType: floorType,
      statuses: statuses
    };

    const url = `${ConfigurationManagerService.settings.BaseURL}db/Incident/${incidentId}/findOrClusterUsersByBboxHistory`;
    return this.api.post<any>(url, param);
  }

  getUserOnFloorPlayback(
    incidentId: string,
    timestamp: number,
    floorPerimeterId: string,
    statuses: string[],
    limit: number,
    page: number
  ): Observable<any> {
    const param = {
      timestamp: timestamp,
      floorPerimeterId: floorPerimeterId,
      statuses: statuses,
      limit: limit,
      page: page
    };

    const url = `${ConfigurationManagerService.settings.BaseURL}db/Incident/${incidentId}/usersOnFloorHistory`;
    return this.api.post<any>(url, param);
  }

  getCameraSessions(cameraId?: string): Observable<any> {
    const where: any = { endTime: null };
    if (cameraId) {
      where.cameraId = cameraId;
    }
    const param = {
      where,
      order: [['id', 'DESC']]
    };
    const url = `${ConfigurationManagerService.settings.BaseURL}db/CameraSession/findAndCountAll`;
    return this.api.post<any>(url, param);
  }
  getCameraSessionHistory(cameraId?: string): Observable<any> {
    const sevenDaysAgo = moment().subtract(7, 'day').toISOString();
    const where: any = {
      endTime: { $ne: null },
      startTime: { $gt: sevenDaysAgo }
    };
    if (cameraId) {
      where.cameraId = cameraId;
    }
    const param = {
      where,
      order: [['cameraSessionId', 'DESC']],
      limit: 10000
    };
    const url = `${ConfigurationManagerService.settings.BaseURL}db/CameraSessionHistory/findAndCountAll`;
    return this.api.post<any>(url, param);
  }
  // get the user without the beacons
  getBuildingStreetUsers(buildingId: string): Observable<any> {
    const url = `${ConfigurationManagerService.settings.TezAddPapiBaseUrl}building/${buildingId}/users/street/`;
    return this.api.get(url);
  }
  getTwillioVoiceCapabilityToken(): Observable<any> {
    const url = ConfigurationManagerService.settings.BaseURL + ConfigurationManagerService.settings.TwillioVoiceCapabilityToken;
    return this.api.get(url);
  }

  getTwilioToken(): Observable<any> {
    const url = ConfigurationManagerService.settings.TezAddPapiBaseUrl + ConfigurationManagerService.settings.twilioToken;
    return this.api.get(url);
  }


  findOrganizationPhone(): Observable<any> {
    const url =
      ConfigurationManagerService.settings.BaseURL +
      ConfigurationManagerService.settings.findOrganizationPhone;

    const body = {
      attributes: ['id', 'number'],
      order: [['organizationId', 'asc']]
    };

    return this.api.post<any>(url, body);
  }
  searchIncidentUsers(incidentId: string, limit: number, page: number, searchText?: string): any {
    const param: any = {
      limit,
      page
    };

    if (searchText) {
      param.name = searchText;
    }
    const url = `${ConfigurationManagerService.settings.BaseURL}db/Incident/${incidentId}/findUsersByName`;
    return this.api.post<any>(url, param);
  }

  callUrl(url: string): Observable<any> {
    return this.api.get<any>(url + '/api/v1/devices/mobile?seen_within_minutes=5');
  }

  findRaspberryPi(params: any = {}): Observable<any> {
    const url = `${ConfigurationManagerService.settings.BaseURL}db/RaspberryPi/findAndCountAll`;
    return this.api.post<any>(url, params);
  }

  getRaspberryPi(id: string | number): Observable<any> {
    const url = `${ConfigurationManagerService.settings.BaseURL}db/RaspberryPi/${id}`;
    return this.api.get<any>(url);
  }

  queryBleWifiDevices(payload: BleWifiQueryRequest): Observable<BleWifiQueryResponse> {
    const baseUrl = ConfigurationManagerService.settings?.BaseURL;
    const endpoint = ConfigurationManagerService.settings?.BleWifiSensorQuery || 'sensor/ble-wifi/query';
    const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    const url = `${cleanBaseUrl}${cleanEndpoint}`;
    return this.api.post<BleWifiQueryResponse>(url, payload);
  }

}

export interface BleWifiDeviceRecord {
  id: string | number;
  sensorId?: string | number | null;
  platform?: string;
  transport?: string;
  externalDeviceId?: string;
  name?: string;
  device_name?: string;
  address?: string;
  rssi?: number;
  txPower?: number;
  connectable?: boolean;
  manufacturerId?: string;
  manufacturer_id?: string;
  manufacturerData?: Record<string, string>;
  host?: string;
  ipAddresses?: string[];
  port?: number;
  serviceType?: string;
  services?: any[];
  beaconUuid?: string;
  major?: number;
  minor?: number;
  eddystoneNs?: string;
  eddystoneId?: string;
  advertisedMac?: string;
  resolvedMac?: string;
  rawData?: string;
  firstSeen?: string;
  lastSeen?: string;
  last_seen?: string;
  createdAt?: string;
  updatedAt?: string;
  incidentId?: string | number;
  perimeterId?: string | number;
  coordinates?: [number, number] | number[];
  deletedAt?: string;
}

export interface BleWifiQueryRequest {
  perimeter_id?: (string | number)[];
  sensor_uuid?: string[];
  incident_id?: string | number | null;
  transport?: string;
  platform?: string;
  search?: string;
  first_seen?: string;
  last_seen?: string;
  first_seen_from?: string;
  first_seen_to?: string;
  last_seen_from?: string;
  last_seen_to?: string;
  sort_by?: string;
  sort_order?: string;
  limit?: number;
  offset?: number;
  page?: number;
}

export interface BleWifiQueryResponse {
  success: boolean;
  message: string;
  data: {
    total: number;
    limit: number;
    offset: number;
    page: number;
    records: BleWifiDeviceRecord[];
  };
}

